import React, { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: "",
      errorStack: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    this.setState({
      errorMessage: error.message || "An unexpected error occurred",
      errorStack: errorInfo.componentStack,
      errorId: errorId,
    });

    // Log error or send to monitoring service
    console.error("Error:", error);
    console.error("Error Info:", errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center p-6">
          <ErrorDisplay
            errorMessage={this.state.errorMessage}
            errorStack={this.state.errorStack}
            errorId={this.state.errorId}
            onReset={() => this.setState({
              hasError: false,
              errorMessage: "",
              errorStack: null,
              errorId: null,
            })}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

const ErrorDisplay = ({ errorMessage, errorStack, errorId, onReset }) => {
  const handleCopy = () => {
    const details = `Error ID: ${errorId}\nMessage: ${errorMessage}\nStack: ${errorStack}\nURL: ${window.location.href}`;
    navigator.clipboard.writeText(details)
      .then(() => alert('Error details copied to clipboard'))
      .catch(() => alert('Failed to copy details'));
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600">
          We've encountered an unexpected error. Please try again.
        </p>
        <div className="mt-2 text-sm text-gray-500">
          Error ID: {errorId}
        </div>
      </div>

      {/* Error Details */}
      <div className="mb-6">
        <details className="group">
          <summary className="flex justify-between items-center cursor-pointer list-none p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              Technical details
            </span>
            <svg
              className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </summary>
          <div className="mt-2 p-4 bg-gray-50 rounded-lg">
            <div className="mb-3">
              <h3 className="text-xs font-medium text-gray-500 mb-1">
                Error message
              </h3>
              <p className="text-sm font-mono text-red-600 p-2 bg-red-50 rounded">
                {errorMessage}
              </p>
            </div>
            {errorStack && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 mb-1">
                  Component stack
                </h3>
                <pre className="text-xs text-gray-600 p-2 bg-gray-100 rounded overflow-auto max-h-40">
                  {errorStack}
                </pre>
              </div>
            )}
          </div>
        </details>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col space-y-3">
        <button
          onClick={onReset}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          className="w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
        >
          Reload page
        </button>
        <button
          onClick={handleCopy}
          className="w-full py-2 px-4 text-sm text-blue-600 hover:text-blue-800 font-medium rounded-lg transition-colors"
        >
          Copy error details
        </button>
      </div>

      {/* Support Link */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Need help?{' '}
          <a
            href="mailto:support@example.com"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
};

export default ErrorBoundary;